/**
 * Unified Template Editor — one screen for create and edit (REQ-4, REQ-5, REQ-6).
 *
 * The whole screen is a DRAFT: nothing touches the server until Save, which sends
 * one request to POST /templates/save. Create opens an empty monday-style table
 * (nothing is read from the current item); "Sync all from this item" is the one
 * explicit action that copies the item's subitems (REQ-2), and it lands in the
 * draft, so Cancel still undoes it.
 */
import { Check, NavigationChevronLeft, Retry } from '@vibe/icons';
import { Modal, ModalBasicLayout, ModalContent, ModalFooter, ModalHeader } from '@vibe/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { canonicalType, isSupportedColumn } from '../../Config';
import { withBoardSettings } from '../../lib/boardSettings';
import { parseColumnValue, serializeRowValues } from '../../lib/columnValues';
import { blankUpdateRowIds, blankUpdatesMessage, unnamedRowIds, unnamedRowsMessage } from '../../lib/rowNames';
import { uniqueTemplateName } from '../../lib/templateNames';
import { accessMembersOf, withCurrentNames } from '../../lib/accessMembers';
import { useAccountPeople, useAccountUsers } from '../../lib/accountPeople';
import { SUBITEM_LIMITS } from '../../lib/templateLimits';
import useBoardColumns, { useBoardMembers } from '../../lib/useBoardColumns';
import useItemBoardColumns from '../../lib/useItemBoardColumns';
import useParentItem from '../../lib/parentItem';
import { countUpdates } from '../../lib/updates';
import Spinner from '../Spinner';
import SubitemsTable from '../SubitemsTable';
import AddColumnMenu from './AddColumnMenu';
import { FILES_PLAN, useFilesPlan } from '../../lib/filesPlan';
import { stillUploadingTitle, useCellUploads, waitForUploadsMessage } from '../../lib/cellUploads';
import CellEditor from './CellEditor';
import ColumnMenu from './ColumnMenu';
import TemplateDetailsCard from './TemplateDetailsCard';
import UpdatesPanel from './UpdatesPanel';
import './TemplateEditor.css';

let localRowSeq = 0;
const newRowId = () => `draft-${Date.now()}-${localRowSeq++}`;
const emptyRow = () => ({ id: newRowId(), name: '', values: {}, updates: [] });

/** Stored updates → draft updates (copied, so editing never touches the stored template). */
const copyUpdates = (updates) =>
  (updates || []).map((u) => ({ body: u.body || '', mentions: (u.mentions || []).map((m) => ({ ...m })) }));

/** Stored template rows → draft rows keyed by column id. */
function draftRowsFromTemplate(template, columns) {
  return (template.subitems || []).map((sub) => {
    const values = {};
    for (const col of columns) {
      const cv = (sub.column_values || []).find((v) => String(v.id) === String(col.id));
      if (cv) {
        const parsed = parseColumnValue(cv, col);
        if (parsed !== null && parsed !== undefined) values[col.id] = parsed;
      }
    }
    return { id: newRowId(), name: sub.name || '', values, updates: copyUpdates(sub.updates) };
  });
}

export default function TemplateEditor({
  monday,
  itemId,
  template, // null → create · with id → edit · without id → create, prefilled (duplicate)
  onSave, // async (payload) => templates
  onCancel,
  takenNames = [], // names of the other templates of this kind; a taken name is saved as "Name (1)"
  files, // lib/files service for File column cells
  onUpgradePlan, // opens monday's plan selection (a host call, so App's)
  viewerId, // the person editing: owns a new template
}) {
  /**
   * The **id** — not the presence of a template — decides edit vs create. A
   * duplicate arrives prefilled but id-less, so it is a create in every respect:
   * same heading, same copy, same "Save template" button, and Save inserts.
   * There is deliberately no third "duplicate" mode to notice or explain.
   */
  const isEdit = Boolean(template && template.id);
  const board = useBoardColumns(monday, itemId);
  // the item's own board and the item itself: what a cell's "From the parent item" rules name and preview
  const itemBoard = useItemBoardColumns(monday, itemId);
  const parentItem = useParentItem(monday, itemId);
  const members = useBoardMembers(monday, itemId); // fetched on first People cell open
  const people = useAccountPeople(monday, { itemId }); // the board's people, fetched when a mention picker first opens
  const filesPlan = useFilesPlan(files); // whether the Add column menu offers File columns
  // File uploads outlive the cell popup (lib/cellUploads); a finished one lands in its cell
  const uploads = useCellUploads(files, {
    keyOf: (c) => `${c.rowId}|${c.columnId}`,
    onDone: (c, file) => appendFile(c.rowId, c.columnId, file),
  });

  const [name, setName] = useState(template ? template.name : '');
  const [accessLevel, setAccessLevel] = useState(template ? template.access_level : 'user_level');
  // who a User level template is shared with (lib/accessMembers); kept while Account level is picked
  const [accessMembers, setAccessMembers] = useState(() => accessMembersOf(template));
  const accountUsers = useAccountUsers(monday);
  // Every mode opens at once; the board loads behind it. Create starts from an
  // empty table. Edit / duplicate start from the stored columns and values, which
  // render without the board — what they lack is the board's column SETTINGS
  // (status colours, dropdown options, rating scale, number unit), merged in by
  // the effect below when the board arrives. Until then status cells are neutral,
  // and the status / dropdown / rating pickers show a loading state.
  const [columns, setColumns] = useState(() =>
    template ? (template.columns || []).map(({ id, title, type }) => ({ id, title, type })) : [],
  );
  const [rows, setRows] = useState(() =>
    template ? draftRowsFromTemplate(template, columns) : [emptyRow()],
  );
  const [dirty, setDirty] = useState(false);
  const [nameError, setNameError] = useState(false);
  // set by a refused Save; from then on unnamed rows are outlined until fixed
  const [showRowErrors, setShowRowErrors] = useState(false);
  const nameInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [popover, setPopover] = useState(null); // {kind, anchorEl, ...}
  const [confirm, setConfirm] = useState(null); // 'cancel' | 'sync'
  const [syncing, setSyncing] = useState(false);
  const [panelRow, setPanelRow] = useState(null); // id of the row whose updates panel is open
  const settingsMerged = useRef(false);

  // Board arrived: attach its settings to the columns already in the draft. Uses
  // the CURRENT columns, not the template's, so a column removed while the board
  // was loading stays removed. Not a user edit, so the draft stays clean.
  useEffect(() => {
    if (settingsMerged.current || board.loading || board.error) return;
    settingsMerged.current = true;
    setColumns((prev) => withBoardSettings(prev, board.columns));
  }, [board.loading, board.error, board.columns]);

  const touch = () => setDirty(true);
  const filledRows = useMemo(() => (rows || []).filter((r) => r.name.trim() !== ''), [rows]);
  const updatesTotal = useMemo(() => countUpdates(filledRows), [filledRows]);
  const unnamed = useMemo(() => unnamedRowIds(rows), [rows]);
  const blankUpdates = useMemo(() => blankUpdateRowIds(rows), [rows]);
  const savedName = useMemo(() => uniqueTemplateName(name, takenNames), [name, takenNames]);
  // Anything worth saving: a name, a named row, or a column.
  const hasContent = name.trim() !== '' || filledRows.length > 0 || (columns || []).length > 0;
  const usedColumnIds = useMemo(
    () => new Set((columns || []).map((c) => String(c.id))),
    [columns],
  );

  const addColumns = (boardCols) => {
    const list = Array.isArray(boardCols) ? boardCols : [boardCols];
    if (!list.length) return;
    setColumns((prev) => {
      const have = new Set(prev.map((c) => String(c.id)));
      return [...prev, ...list.filter((c) => !have.has(String(c.id)))];
    });
    touch();
  };
  const removeColumn = (col) => {
    uploads.cancelWhere((c) => c.columnId === String(col.id));
    setColumns((prev) => prev.filter((c) => String(c.id) !== String(col.id)));
    setRows((prev) =>
      prev.map((r) => {
        const values = { ...r.values };
        delete values[col.id];
        return { ...r, values };
      }),
    );
    touch();
  };
  const clearColumnValues = (col) => {
    uploads.cancelWhere((c) => c.columnId === String(col.id));
    setRows((prev) =>
      prev.map((r) => {
        const values = { ...r.values };
        delete values[col.id];
        return { ...r, values };
      }),
    );
    touch();
  };
  const setCellValue = (rowId, colId, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const values = { ...r.values };
        if (value === null || value === undefined) delete values[colId];
        else values[colId] = value;
        return { ...r, values };
      }),
    );
    touch();
  };
  // a finished upload lands in its cell, whether or not the popup is still open
  const appendFile = (rowId, colId, file) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== rowId) return r;
        const current = (r.values[colId] && r.values[colId].files) || [];
        return { ...r, values: { ...r.values, [colId]: { files: [...current, file] } } };
      }),
    );
    touch();
  };
  const leaveCancellingUploads = () => {
    uploads.cancelAll();
    onCancel();
  };
  const setRowName = (rowId, value) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, name: value } : r)));
    touch();
  };
  const deleteRow = (rowId) => {
    uploads.cancelWhere((c) => c.rowId === rowId);
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    touch();
  };
  const reorderRows = (from, to) => {
    setRows((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    touch();
  };
  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
    touch();
  };
  const setRowUpdates = (rowId, updates) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, updates } : r)));
    touch();
  };
  const panelRowObj = panelRow ? rows.find((r) => r.id === panelRow) : null;

  // ---- Sync all from this item (REQ-2: replace, confirm when non-empty) ------
  const runSync = async () => {
    setConfirm(null);
    setSyncing(true);
    try {
      const res = await monday.api(
        `query { items(ids:${itemId}) { subitems { id name column_values { id value text type } } } }`,
      );
      const subitems = res.data.items[0].subitems || [];

      // Columns those values need (supported + known on the board), keeping current ones.
      // A File column only when the plan includes files, as in the Add column menu.
      const skipFiles = filesPlan !== FILES_PLAN.AVAILABLE;
      const nextColumns = [...(columns || [])];
      const have = new Set(nextColumns.map((c) => String(c.id)));
      for (const sub of subitems) {
        for (const cv of sub.column_values || []) {
          if (have.has(String(cv.id)) || !isSupportedColumn(cv.type)) continue;
          if (skipFiles && canonicalType(cv.type) === 'file') continue;
          if (cv.value === null || cv.value === undefined || cv.value === '') continue;
          const boardCol = board.columns.find((bc) => String(bc.id) === String(cv.id));
          if (boardCol) {
            nextColumns.push(boardCol);
            have.add(String(boardCol.id));
          }
        }
      }

      const nextRows = subitems.map((sub) => {
        const values = {};
        for (const col of nextColumns) {
          const cv = (sub.column_values || []).find((v) => String(v.id) === String(col.id));
          if (cv) {
            const parsed = parseColumnValue(cv, col);
            if (parsed !== null && parsed !== undefined) values[col.id] = parsed;
          }
        }
        // The item's existing updates are not copied (requirements F1.9, P2).
        return { id: newRowId(), name: sub.name || '', values, updates: [] };
      });

      uploads.cancelAll(); // the rows they were for are replaced
      setColumns(nextColumns);
      setRows(nextRows.length ? nextRows : [emptyRow()]);
      touch();
    } finally {
      setSyncing(false);
    }
  };
  const onSyncClick = () => {
    if (filledRows.length || (columns || []).length) setConfirm('sync');
    else runSync();
  };

  // ---- save ------------------------------------------------------------------
  const save = async () => {
    if (uploads.running > 0) return; // the button says to wait
    if (name.trim() === '') {
      setNameError(true);
      if (nameInputRef.current) nameInputRef.current.focus();
      return;
    }
    setNameError(false);
    // a row with values but no name, or an update left empty, would be dropped
    // without a word: refuse instead, and open the first empty update
    if (unnamed.size > 0 || blankUpdates.size > 0) {
      setShowRowErrors(true);
      if (blankUpdates.size > 0 && !(panelRow && blankUpdates.has(panelRow))) {
        setPanelRow(rows.find((r) => blankUpdates.has(r.id)).id);
      }
      return;
    }
    setSaving(true);
    try {
      const payload = {
        id: isEdit ? template.id : undefined,
        name: savedName,
        access_level: accessLevel,
        access_members: withCurrentNames(accessMembers, accountUsers.users),
        columns: columns.map((c) => ({ id: c.id, title: c.title, type: c.type })),
        subitems: filledRows.map((r) => ({
          id: r.id,
          name: r.name.trim(),
          column_values: serializeRowValues(columns, r.values),
          // an update left empty in the panel is dropped, like a blank row
          updates: (r.updates || []).filter((u) => u.body && u.body.trim() !== ''),
        })),
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    if (uploads.running > 0) setConfirm('uploads');
    else if (dirty) setConfirm('cancel');
    else onCancel();
  };

  // ---- render ----------------------------------------------------------------
  // No full-page loading or error state: the draft never depends on the board to
  // render. Board progress shows in the Subitems card header and in the pickers
  // that need its settings; a failure is a banner, and stored values stay editable.

  const saveLabel = isEdit ? 'Save changes' : 'Save template';
  const heading = isEdit ? 'Edit template' : 'New template';
  const subheading = isEdit
    ? 'Change details, columns, values and order — all saved together in one action.'
    : 'Author the subitems and values this template should create. Nothing is read from the current item unless you sync it.';

  return (
    <>
      <div>
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={cancel}
            className="inline-flex items-center mb-4 text-sm font-medium text-secondary transition-colors hover:text-primary"
          >
            <NavigationChevronLeft className="w-4 h-4 mr-2" />
            Back to Templates
          </button>
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-primary">{heading}</h1>
            {/* Cancel / Save live in the footer only — one pair, one place */}
            {dirty && (
              <span className="text-sm font-medium text-warning-ink">● Unsaved changes</span>
            )}
          </div>
          <p className="mt-2 text-sm text-secondary">{subheading}</p>
        </div>

        <TemplateDetailsCard
          name={name}
          savedAs={savedName !== name.trim() ? savedName : null}
          nameError={nameError}
          nameInputRef={nameInputRef}
          accessLevel={accessLevel}
          accessMembers={accessMembers}
          ownerId={isEdit && template.owner_id != null ? template.owner_id : viewerId}
          viewerId={viewerId}
          people={accountUsers}
          onAccessMembersChange={(members) => {
            setAccessMembers(members);
            touch();
          }}
          onNameChange={(v) => {
            setName(v);
            if (v.trim() !== '') setNameError(false);
            touch();
          }}
          onAccessChange={(v) => {
            setAccessLevel(v);
            touch();
          }}
        />

        {/* Subitems & columns */}
        <div className="mb-6 bg-surface border border-subtle rounded-lg shadow-sm">
          <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-b border-subtle">
            <h2 className="spt-label">Subitems</h2>
            <span className="text-sm text-secondary">
              {filledRows.length} row{filledRows.length === 1 ? '' : 's'} · {columns.length} column
              {columns.length === 1 ? '' : 's'} · {updatesTotal} update{updatesTotal === 1 ? '' : 's'}
            </span>
            {board.loading && (
              <span className="inline-flex items-center gap-2 text-sm text-secondary" role="status">
                <Spinner />
                Reading your board columns and column settings
              </span>
            )}
            <span className="flex-1" />
            {/* Columns are added with the + at the end of the table's header row */}
            {/* Sync adds the columns the item's values need, so it waits for the board */}
            <button
              onClick={onSyncClick}
              data-tour="sync"
              disabled={syncing || board.loading || Boolean(board.error)}
              title={
                board.loading
                  ? 'Reading your board columns and column settings'
                  : board.error
                    ? "Could not read this board's subitem columns"
                    : undefined
              }
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-secondary rounded-md hover:bg-hover disabled:opacity-50"
            >
              {syncing ? <Spinner className="mr-2" /> : <Retry className="w-4 h-4 mr-2" />}
              Sync all from this item
            </button>
          </div>
          {board.error && (
            <div className="px-6 py-3 text-sm border-b border-subtle text-primary bg-sunken" role="alert">
              Could not read this board's subitem columns, so colours and label lists are
              unavailable. Your template's values are still here and can be saved. Refresh to try
              again.
            </div>
          )}
          <div className="p-4" style={{ maxHeight: 480, overflowY: 'auto' }} data-tour="table">
            <SubitemsTable
              columns={columns}
              subitems={rows.map((r) => ({
                id: r.id,
                name: r.name,
                column_values: serializeRowValues(columns, r.values),
                updates: r.updates || [],
              }))}
              readOnly={false}
              invalidNameIds={showRowErrors ? unnamed : undefined}
              invalidUpdateIds={showRowErrors ? blankUpdates : undefined}
              parentColumns={itemBoard.columns}
              parent={parentItem.parent}
              onOpenUpdates={setPanelRow}
              onNameChange={setRowName}
              onRowDelete={deleteRow}
              onRowsReorder={reorderRows}
              onAddRow={addRow}
              cellUploads={(rowId, column) => uploads.forCell({ rowId, columnId: String(column.id) })}
              onCellClick={(rowId, column, anchorEl) => {
                if (canonicalType(column.type) === 'checkbox') {
                  const row = rows.find((r) => r.id === rowId);
                  setCellValue(rowId, column.id, row && row.values[column.id] ? null : true);
                  return;
                }
                setPopover({ kind: 'cell', rowId, column, anchorEl });
              }}
              onAddColumnClick={(anchorEl) => setPopover({ kind: 'addcol', anchorEl })}
              onColumnMenuClick={(column, anchorEl) => setPopover({ kind: 'colmenu', column, anchorEl })}
            />
            {showRowErrors && unnamed.size > 0 && (
              <p className="mt-2 text-sm text-danger-ink" role="alert">
                {unnamedRowsMessage(unnamed.size, 'subitem')}
              </p>
            )}
            {showRowErrors && blankUpdates.size > 0 && (
              <p className="mt-2 text-sm text-danger-ink" role="alert">
                {blankUpdatesMessage(blankUpdates.size, 'subitem')}
              </p>
            )}
            <p className="mt-2 text-xs text-secondary">
              Blank rows are ignored on save. Every row with values needs a name. Drag the handle to reorder.
            </p>
          </div>
        </div>

        {/*
          Footer actions. Hand-rolled buttons, NOT Vibe's <Button>: Vibe renders a
          4px radius while every other button in this app is Tailwind `rounded-md`
          (6px), so the pair stood out. These reuse the exact classes the template
          detail view uses for its secondary/primary buttons.

          Cancel sits immediately left of Save rather than at the far edge — the
          two are one decision, so they belong as one group.
        */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary transition-colors bg-surface border border-strong rounded-md shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            data-tour="save"
            // Enabled whenever the draft holds anything, name or not — clicking it
            // without a name is how the user finds out the name is required.
            // Off while files upload: saving then would leave them out.
            disabled={saving || !hasContent || uploads.running > 0}
            title={uploads.running > 0 ? waitForUploadsMessage(uploads.running) : undefined}
            aria-busy={saving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-on-accent transition-colors bg-accent border border-transparent rounded-md shadow-sm hover:bg-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <Spinner className="mr-2" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>

      {/* Popovers */}
      {popover && popover.kind === 'addcol' && (
        <AddColumnMenu
          anchorEl={popover.anchorEl}
          boardColumns={board.columns}
          loading={board.loading}
          error={board.error}
          usedIds={usedColumnIds}
          filesPlan={filesPlan}
          onUpgrade={onUpgradePlan}
          onAdd={addColumns}
          onRemove={removeColumn}
          onClose={() => setPopover(null)}
        />
      )}
      {popover && popover.kind === 'colmenu' && (
        <ColumnMenu
          anchorEl={popover.anchorEl}
          column={popover.column}
          onClearValues={clearColumnValues}
          onRemoveColumn={removeColumn}
          onClose={() => setPopover(null)}
        />
      )}
      {popover && popover.kind === 'cell' && (
        <CellEditor
          anchorEl={popover.anchorEl}
          // the live column, not the one captured at click time — it gains its
          // settings if the board arrives while the picker is open
          column={columns.find((c) => String(c.id) === String(popover.column.id)) || popover.column}
          boardLoading={board.loading}
          boardError={Boolean(board.error)}
          users={members.users}
          usersError={members.error}
          usersLoading={members.status !== 'loaded'}
          onNeedUsers={members.load}
          files={files}
          uploads={uploads}
          cell={{ rowId: popover.rowId, columnId: String(popover.column.id) }}
          parentColumns={itemBoard.columns}
          parent={parentItem.parent}
          value={(rows.find((r) => r.id === popover.rowId) || { values: {} }).values[popover.column.id]}
          onChange={(v) => setCellValue(popover.rowId, popover.column.id, v)}
          onClose={() => setPopover(null)}
        />
      )}

      {/* Updates of one row — a side panel; its edits land in the draft like any cell */}
      {panelRowObj && (
        <UpdatesPanel
          row={panelRowObj}
          limits={SUBITEM_LIMITS}
          people={people}
          showErrors={showRowErrors}
          onChange={(updates) => setRowUpdates(panelRowObj.id, updates)}
          onClose={() => setPanelRow(null)}
        />
      )}

      {/* Confirm: sync replaces the table */}
      <Modal id="spt-editor-confirm-sync" show={confirm === 'sync'} onClose={() => setConfirm(null)} size="small">
        <ModalBasicLayout>
          <ModalHeader title="Sync all from this item?" />
          <ModalContent>
            <p>
              Your table will be replaced with this item's subitems, and any columns their values
              need will be added. Nothing is saved until you click {saveLabel} — Cancel still
              undoes this.
            </p>
          </ModalContent>
        </ModalBasicLayout>
        <ModalFooter
          primaryButton={{ text: 'Sync all', onClick: runSync }}
          secondaryButton={{ text: 'Keep my table', onClick: () => setConfirm(null) }}
        />
      </Modal>

      {/* Confirm: leave while files are uploading */}
      <Modal id="spt-editor-confirm-uploads" show={confirm === 'uploads'} onClose={() => setConfirm(null)} size="small">
        <ModalBasicLayout>
          <ModalHeader title={stillUploadingTitle(uploads.running)} />
          <ModalContent>
            <p>{dirty ? 'Leave anyway? The uploads are cancelled and your changes are lost.' : 'Leave anyway? The uploads are cancelled and nothing in the template changes.'}</p>
          </ModalContent>
        </ModalBasicLayout>
        <ModalFooter
          primaryButton={{ text: 'Leave', color: 'negative', onClick: leaveCancellingUploads }}
          secondaryButton={{ text: 'Keep editing', onClick: () => setConfirm(null) }}
        />
      </Modal>

      {/* Confirm: discard draft */}
      <Modal id="spt-editor-confirm-cancel" show={confirm === 'cancel'} onClose={() => setConfirm(null)} size="small">
        <ModalBasicLayout>
          <ModalHeader title="Discard changes?" />
          <ModalContent>
            <p>Your columns, values and ordering will be lost.</p>
          </ModalContent>
        </ModalBasicLayout>
        <ModalFooter
          primaryButton={{ text: 'Discard', color: 'negative', onClick: onCancel }}
          secondaryButton={{ text: 'Keep editing', onClick: () => setConfirm(null) }}
        />
      </Modal>
    </>
  );
}
